import React, { useMemo } from 'react';
import { Box, IconButton, List, ListItem } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import {
  getTasksData,
  getTaskState,
} from '../../redux/reducer/coreScriptReducer';
import { StateTaskDataTypes } from '@safeguardproperties/corescript-react-npm/dist/types';
import CustomTypography from '../../ui/atoms/CustomTypography';

const keyToString = (value?: string): string => value?.toLanguagePack() ?? '';

const BID_MODES = new Set(['BidProposal', 'OrderedBidProposal']);

// Element id of a task accordion in TaskControl, used to scroll to it.
export const taskAnchorId = (taskId: string): string => `task-${taskId}`;

const scrollToTask = (taskId: string) => {
  document
    .getElementById(taskAnchorId(taskId))
    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

// Count of a task's items still needing attention: required questions not yet
// completed (or in error) plus media labels whose minimum isn't met.
const taskIncompleteCount = (task?: StateTaskDataTypes): number => {
  if (!task) {
    return 0;
  }
  let count = 0;
  for (const question of task.questions ?? []) {
    if (
      (question.Required && !question.Completed) ||
      question.ErrorType === 'error'
    ) {
      count += 1;
    }
  }
  const list = task.media?.List ?? {};
  for (const media of Object.values(list)) {
    if ((media.Min ?? 0) > 0 && !media.Complete) {
      count += 1;
    }
  }
  return count;
};

type TaskSidePanelProps = {
  mode: 'tasks' | 'bids';
  onClose: () => void;
  expanded?: boolean;
};

/**
 * TaskSidePanel mirrors the questions SidePanel for the Tasks/Bids tabs: a list
 * of the added task instances (matching the active mode) with an orange badge
 * counting each task's incomplete items. Clicking a row scrolls to that task.
 */
const TaskSidePanel: React.FC<TaskSidePanelProps> = ({
  mode,
  onClose,
  expanded,
}) => {
  const tasksData = useSelector(getTasksData);
  const taskState = useSelector(getTaskState);
  const isBids = mode === 'bids';

  const taskIds = useMemo(
    () =>
      Object.keys(tasksData ?? {}).filter((taskId) => {
        const modeType = taskState[taskId]?.modeType;
        const taskIsBid = !!modeType && BID_MODES.has(modeType);
        return isBids ? taskIsBid : !taskIsBid;
      }),
    [tasksData, taskState, isBids]
  );

  return (
    <Box
      sx={{
        height: '100%',
        maxWidth: expanded ? '100%' : '300px',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <List>
        {taskIds.length === 0 && (
          <ListItem>
            <CustomTypography
              variant='body1'
              sx={{
                fontFamily: 'Montserrat',
                fontSize: '13px',
                color: '#666',
              }}
            >
              {isBids ? 'No bids added.' : 'No tasks added.'}
            </CustomTypography>
          </ListItem>
        )}
        {taskIds.map((taskId) => {
          const task = tasksData[taskId];
          const taskTitle = task?._taskName || task?.DisplayName || taskId;
          const incomplete = taskIncompleteCount(taskState[taskId]);

          return (
            <ListItem
              key={taskId}
              onClick={() => scrollToTask(taskId)}
              sx={{ cursor: 'pointer' }}
            >
              <CustomTypography
                variant='h5'
                sx={{
                  position: 'relative',
                  color: '#343330',
                  fontFamily: 'Montserrat',
                  fontSize: '16px',
                  fontWeight: 600,
                  textTransform: 'capitalize',
                  lineHeight: '21.94px',
                  textAlign: 'left',
                }}
              >
                {keyToString(taskTitle)}
                {incomplete > 0 && (
                  <Box
                    component='span'
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '20px',
                      height: '20px',
                      backgroundColor: '#F35625',
                      borderRadius: '50%',
                      color: '#fff',
                      fontSize: '11px',
                      marginLeft: '8px',
                    }}
                  >
                    {incomplete}
                  </Box>
                )}
              </CustomTypography>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
};

export default TaskSidePanel;
